import { AgentReferenceType, ClientAgentReference, ClientFileReference } from '@/agent/references/store/schemas'
import { matterPaths } from '@/routing/paths'
import { buildLegacyFileViewerUrl } from '@/standalone-reference-viewer/legacy-file/utils/build-legacy-file-viewer-url'

export enum FileReferenceTargetKind {
  CANONICAL = 'canonical',
  LEGACY = 'legacy',
  NONE = 'none',
}

export type CanonicalFileReferenceTarget = {
  kind: FileReferenceTargetKind.CANONICAL
  viewerUrl: string | null
  fileReference: ClientFileReference | null
  userFileId: string | null
  startingPage?: number
}

export type LegacyFileReferenceTarget = {
  kind: FileReferenceTargetKind.LEGACY
  filePath: string
  legacyViewerUrl: string | null
  startingPage?: number
}

export type NoFileReferenceTarget = {
  kind: FileReferenceTargetKind.NONE
}

export type FileReferenceTarget = CanonicalFileReferenceTarget | LegacyFileReferenceTarget | NoFileReferenceTarget

export type ResolveFileReferenceTargetInput = {
  reference: ClientAgentReference
  matterId: string | null
}

export function resolveFileReferenceTarget(input: ResolveFileReferenceTargetInput): FileReferenceTarget {
  const { reference, matterId } = input

  if (reference.type === AgentReferenceType.USER_FILE_QDRANT_CHUNK) {
    return canonicalTarget(reference, matterId)
  }

  if (reference.type === AgentReferenceType.ASSET_EVENT) {
    return assetEventTarget(reference, matterId)
  }

  if (!isLegacyFileUploadReference(reference)) {
    return { kind: FileReferenceTargetKind.NONE }
  }

  return legacyTarget(reference, matterId) ?? { kind: FileReferenceTargetKind.NONE }
}

export function isFileBackedReference(reference: ClientAgentReference): boolean {
  return (
    reference.type === AgentReferenceType.USER_FILE_QDRANT_CHUNK || reference.type === AgentReferenceType.ASSET_EVENT || isLegacyFileUploadReference(reference)
  )
}

function isLegacyFileUploadReference(
  reference: ClientAgentReference
): reference is Extract<ClientAgentReference, { type: AgentReferenceType.FILE_UPLOAD_CHUNK | AgentReferenceType.FILE_UPLOAD_FULL }> {
  return reference.type === AgentReferenceType.FILE_UPLOAD_CHUNK || reference.type === AgentReferenceType.FILE_UPLOAD_FULL
}

function canonicalTarget(
  reference: Extract<ClientAgentReference, { type: AgentReferenceType.USER_FILE_QDRANT_CHUNK }>,
  matterId: string | null
): CanonicalFileReferenceTarget {
  const fileReference = reference.file_reference ?? null
  const userFileId = fileReference?.user_file_id ?? reference.metadata.user_file_id
  const oneBasedStartingPage = fileReference?.page_nums?.[0] ?? reference.metadata.page_nums?.[0]

  return {
    kind: FileReferenceTargetKind.CANONICAL,
    viewerUrl: buildCanonicalViewerUrl(reference, matterId),
    fileReference,
    userFileId,
    startingPage: oneBasedPageToZeroBased(oneBasedStartingPage),
  }
}

function assetEventTarget(
  reference: Extract<ClientAgentReference, { type: AgentReferenceType.ASSET_EVENT }>,
  matterId: string | null
): CanonicalFileReferenceTarget {
  const fileReference = reference.file_reference ?? null
  return {
    kind: FileReferenceTargetKind.CANONICAL,
    viewerUrl: buildCanonicalViewerUrl(reference, matterId),
    fileReference,
    userFileId: fileReference?.user_file_id ?? reference.metadata.user_file_id,
    startingPage: fileReference?.page_nums?.[0] ?? reference.metadata.page_nums?.[0],
  }
}

function legacyTarget(
  reference: Extract<ClientAgentReference, { type: AgentReferenceType.FILE_UPLOAD_CHUNK | AgentReferenceType.FILE_UPLOAD_FULL }>,
  matterId: string | null
): LegacyFileReferenceTarget | null {
  const filePath = reference.metadata.file_path
  if (!filePath) return null

  return {
    kind: FileReferenceTargetKind.LEGACY,
    filePath,
    legacyViewerUrl: buildLegacyFileViewerUrl(filePath, matterId),
    startingPage: reference.type === AgentReferenceType.FILE_UPLOAD_CHUNK ? reference.metadata.page_nums?.[0] : undefined,
  }
}

function buildCanonicalViewerUrl(reference: ClientAgentReference, matterId: string | null): string | null {
  if (!matterId) return null
  return matterPaths.referenceViewer.userFile(matterId, encodeURIComponent(reference.reference.id))
}

function oneBasedPageToZeroBased(page: number | undefined): number | undefined {
  if (page === undefined) return undefined
  return Math.max(page - 1, 0)
}
